import { useRouter } from "expo-router";

import { ResourceListScreen } from "~/components/composed/ResourceListScreen";
import { Button, ButtonText } from "~/components/ui";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceProjects,
  useWorkspaceTasks,
} from "~/hooks/workspace";
import {
  TASK_PRIORITY_OPTIONS,
  taskPriorityLabel,
} from "~/utils/work-planning";

const TASK_STATUSES = [
  { label: "To do", value: "todo" },
  { label: "Doing", value: "doing" },
  { label: "Done", value: "done" },
  { label: "Archive", value: "archived" },
] as const;

const BILLING_OPTIONS = [
  { label: "Billable", value: "true" },
  { label: "Non-billable", value: "false" },
];

function dueLabel(dueOn: string | null) {
  if (!dueOn) return "No due date";
  const today = new Date().toISOString().slice(0, 10);
  return dueOn < today ? `Overdue ${dueOn}` : `Due ${dueOn}`;
}

export default function TasksScreen() {
  const router = useRouter();
  const workspace = usePrimaryWorkspace();
  const { readOnly } = useWorkspaceReadOnly(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const records = tasks.data ?? [];
  const clientRecords = clients.data ?? [];
  const projectRecords = projects.data ?? [];
  const clientById = new Map(
    clientRecords.map((client) => [client.id, client]),
  );
  const projectById = new Map(
    projectRecords.map((project) => [project.id, project]),
  );
  const projectOptions = [
    { label: "No project", value: "" },
    ...projectRecords.map((project) => ({
      label: project.name,
      value: project.id,
    })),
  ];

  return (
    <ResourceListScreen
      title="Tasks"
      subtitle="Plan and track work items with status, priority, due dates, and project links."
      primaryAction="Add task"
      emptyTitle="No tasks yet"
      emptyDescription="Add a task to start shaping billable work into trackable pieces."
      fields={[
        { key: "name", label: "Task name", placeholder: "Draft homepage copy" },
        {
          key: "projectId",
          label: "Project",
          placeholder: "No project",
          options: projectOptions,
          selectTitle: "Choose project",
        },
        {
          key: "priority",
          label: "Priority",
          placeholder: "Medium",
          options: TASK_PRIORITY_OPTIONS,
          selectTitle: "Choose priority",
        },
        {
          key: "dueOn",
          label: "Due on",
          placeholder: "2026-07-20",
        },
        {
          key: "billable",
          label: "Billing",
          placeholder: "Billable",
          options: BILLING_OPTIONS,
          selectTitle: "Choose billing intent",
        },
        {
          key: "description",
          label: "Description",
          placeholder: "Acceptance notes, links, or reminders",
          multiline: true,
        },
      ]}
      requiredField="name"
      readOnly={readOnly}
      metrics={[
        {
          label: "Open tasks",
          value: String(
            records.filter(
              (task) => task.status !== "done" && task.status !== "archived",
            ).length,
          ),
        },
        {
          label: "High priority",
          value: String(
            records.filter(
              (task) => task.priority === "high" || task.priority === "urgent",
            ).length,
          ),
        },
        {
          label: "Overdue",
          value: String(
            records.filter(
              (task) =>
                task.due_on &&
                task.due_on < new Date().toISOString().slice(0, 10) &&
                task.status !== "done" &&
                task.status !== "archived",
            ).length,
          ),
        },
      ]}
      items={records.map((task) => {
        const project = task.project_id
          ? projectById.get(task.project_id)
          : null;
        const client = project?.client_id
          ? clientById.get(project.client_id)
          : null;
        return {
          id: task.id,
          title: task.name,
          description: task.description ?? undefined,
          meta: [
            client?.name ?? "No client",
            project?.name ?? "No project",
            taskPriorityLabel(task.priority),
            dueLabel(task.due_on),
            task.status,
            task.billable ? "Billable" : "Non-billable",
          ],
          editValues: readOnly
            ? undefined
            : {
                description: task.description ?? "",
                dueOn: task.due_on ?? "",
                name: task.name,
                priority: task.priority,
                projectId: task.project_id ?? "",
                billable: task.billable ? "true" : "false",
              },
          actions: (
            <>
              <Button
                compact
                variant="outline"
                onPress={() => router.push(`/tasks/${task.id}` as never)}
              >
                <ButtonText>View details</ButtonText>
              </Button>
              {!readOnly ? (
                <>
                  {TASK_STATUSES.map((status) => (
                    <Button
                      key={status.value}
                      compact
                      variant={
                        task.status === status.value ? "primary" : "outline"
                      }
                      disabled={tasks.updateStatus.isPending}
                      onPress={() =>
                        void tasks.updateStatus.mutateAsync({
                          id: task.id,
                          status: status.value,
                        })
                      }
                    >
                      <ButtonText>{status.label}</ButtonText>
                    </Button>
                  ))}
                  <Button
                    compact
                    variant="outline"
                    disabled={tasks.setBillable.isPending}
                    onPress={() =>
                      void tasks.setBillable.mutateAsync({
                        id: task.id,
                        billable: !task.billable,
                      })
                    }
                  >
                    <ButtonText>
                      {task.billable ? "Make non-billable" : "Make billable"}
                    </ButtonText>
                  </Button>
                </>
              ) : null}
            </>
          ),
        };
      })}
      loading={
        workspace.isLoading ||
        clients.isLoading ||
        projects.isLoading ||
        tasks.isLoading
      }
      error={
        workspace.error ??
        clients.error ??
        projects.error ??
        tasks.error ??
        tasks.create.error ??
        tasks.update.error ??
        tasks.updateStatus.error ??
        tasks.setBillable.error
      }
      creating={tasks.create.isPending}
      onCreate={(values) => tasks.create.mutateAsync(values)}
      updating={tasks.update.isPending}
      onUpdate={(id, values) => tasks.update.mutateAsync({ id, values })}
    />
  );
}
